// Parity P5 (hybrid design D8, §6.2): recurring work as people control it.
//   - `SeatRoutines`: Chart › seat › Recurring work — every routine of the seat with its
//     cadence, responsibility, next run and who does it; managers get the controls on hover or
//     focus: pause / resume, skip the next occurrence, run now, take over (this occurrence or
//     all future ones; the seat's own holder hands it back), history; and Add routine.
//   - `RepeatDialog`: the task drawer's Repeat… — this task as the template of a routine on a
//     seat (a responsibility of the seat, or a new one), on a cadence or N days after the last
//     one is done. The drawer opens it through `openRepeat` (one host per drawer: `RepeatHost`).
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HistoryIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  RepeatIcon,
  SkipForwardIcon,
  UserRoundCogIcon,
} from "lucide-react";
import { type FormEvent, useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { responsibilitiesQuery, seatsQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { RowAction, RowActions } from "../settings/row-actions";
import { describeSchedule, formatInZone } from "../structure/rrule";
import { boardsQuery } from "../work/work-queries";
import {
  CADENCES,
  type CadenceId,
  cadenceBody,
  processKeys,
  type Routine,
  routineHistoryQuery,
  routinesQuery,
} from "./automation-queries";

const OUTCOME_TEXT: Record<string, string> = {
  materialised: "Task created",
  skipped: "Skipped",
  paused: "Paused",
  resumed: "Resumed",
  taken_over: "Taken over",
};

function useOpenSeats(api: Api, co: string) {
  const q = useInfiniteQuery(seatsQuery(api, co));
  return (q.data?.pages.flatMap((p) => p.data) ?? []).filter((s) => s.status !== "closed");
}

export function SeatRoutines({
  api,
  company,
  seatId,
  readOnly,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  readOnly: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(routinesQuery(api, co, seatId));
  const [adding, setAdding] = useState(false);
  const [taking, setTaking] = useState<Routine | null>(null);
  const [history, setHistory] = useState<Routine | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: processKeys.routines(co, seatId) });
  const fail = (what: string) => (e: unknown) =>
    toast.add({ title: what, description: problemText(e, "Try again"), type: "error" });
  const control = useMutation({
    mutationFn: async ({ r, op }: { r: Routine; op: "pause" | "resume" | "skip" | "run" }) => {
      const params = { co, id: r.recurring_work.id };
      if (op === "pause") await api.client.recurringWork.pause({ params });
      else if (op === "resume") await api.client.recurringWork.resume({ params });
      else if (op === "skip") await api.client.recurringWork.skip({ params });
      else await api.client.recurringWork.materialiseNow({ params });
    },
    onSuccess: (_, { op }) => {
      refresh();
      toast.add({
        title: {
          pause: "Paused",
          resume: "Resumed",
          skip: "Next one skipped",
          run: "Task created",
        }[op],
        type: "success",
      });
    },
    onError: fail("That did not work"),
  });
  const tz = company.calendar?.timezone ?? "UTC";
  const rows = q.data?.data ?? [];

  if (q.isPending) return <Loading label="Loading recurring work" rows={2} />;
  if (q.isError) return <LoadError what="Recurring work" error={q.error} />;
  return (
    <div className="flex flex-col gap-3" data-slot="seat-recurring">
      {!rows.length ? (
        <EmptyState
          variant="compact"
          icon={RepeatIcon}
          title="No recurring work yet"
          description="A routine creates the same task on a cadence, for this seat."
          {...(readOnly
            ? {}
            : {
                actions: (
                  <Button size="sm" onClick={() => setAdding(true)}>
                    <PlusIcon aria-hidden="true" />
                    Add routine
                  </Button>
                ),
              })}
        />
      ) : (
        <Table>
          <TableCaption className="sr-only">Recurring work of this seat</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Task</TableHead>
              <TableHead scope="col">Cadence</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Responsibility
              </TableHead>
              <TableHead scope="col">Next</TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col" className="w-40">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const w = r.recurring_work;
              const paused = w.status === "paused";
              const after = r.control?.after_completion_days;
              return (
                <TableRow key={w.id} data-recurring={w.id} className="group/row">
                  <TableHead scope="row" className="font-medium">
                    {w.task_template.title}
                    {r.taken_over_by_name ? (
                      <span className="block text-xs font-normal text-muted-foreground">
                        Taken over by {r.taken_over_by_name}
                      </span>
                    ) : null}
                  </TableHead>
                  <TableCell>
                    {after
                      ? `${after} days after the last one is done`
                      : describeSchedule(w.schedule)}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-md:hidden">
                    {r.responsibility.name}
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {paused
                      ? "—"
                      : w.next_run_at
                        ? formatInZone(w.next_run_at, tz)
                        : after
                          ? "When the open one is done"
                          : "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={paused ? "warning" : "success"}>
                      {paused ? "Paused" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <RowActions>
                      <RowAction
                        icon={HistoryIcon}
                        label={`History of ${w.task_template.title}`}
                        tip="History"
                        onClick={() => setHistory(r)}
                      />
                      {readOnly ? null : (
                        <>
                          <RowAction
                            icon={paused ? PlayIcon : PauseIcon}
                            label={`${paused ? "Resume" : "Pause"} ${w.task_template.title}`}
                            tip={paused ? "Resume" : "Pause"}
                            disabled={control.isPending}
                            onClick={() => control.mutate({ r, op: paused ? "resume" : "pause" })}
                          />
                          <RowAction
                            icon={SkipForwardIcon}
                            label={`Skip the next ${w.task_template.title}`}
                            tip="Skip the next one"
                            disabled={paused || !w.next_run_at || control.isPending}
                            onClick={() => control.mutate({ r, op: "skip" })}
                          />
                          <RowAction
                            icon={UserRoundCogIcon}
                            label={`Take over ${w.task_template.title}`}
                            tip="Take over"
                            onClick={() => setTaking(r)}
                          />
                          <Button
                            variant="ghost"
                            size="xs"
                            aria-label={`Run ${w.task_template.title} now`}
                            disabled={paused || !w.next_run_at || control.isPending}
                            onClick={() => control.mutate({ r, op: "run" })}
                          >
                            Run now
                          </Button>
                        </>
                      )}
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {rows.length && !readOnly ? (
        <span>
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <PlusIcon aria-hidden="true" />
            Add routine
          </Button>
        </span>
      ) : null}
      {adding ? (
        <AddRoutineDialog
          api={api}
          company={company}
          seatId={seatId}
          onClose={() => setAdding(false)}
        />
      ) : null}
      {taking ? (
        <TakeOverDialog
          api={api}
          company={company}
          routine={taking}
          onClose={() => setTaking(null)}
        />
      ) : null}
      {history ? (
        <RoutineHistory
          api={api}
          company={company}
          routine={history}
          onClose={() => setHistory(null)}
        />
      ) : null}
    </div>
  );
}

/** The cadence picker both forms share. */
function CadenceFields({
  cadence,
  days,
  onCadence,
  onDays,
}: {
  cadence: CadenceId;
  days: number;
  onCadence: (c: CadenceId) => void;
  onDays: (n: number) => void;
}) {
  const id = useId();
  return (
    <Field>
      <FieldLabel htmlFor={id}>Repeats</FieldLabel>
      <span className="flex items-center gap-2">
        <NativeSelect
          id={id}
          value={cadence}
          onChange={(e) => onCadence(e.target.value as CadenceId)}
        >
          {CADENCES.map((c) => (
            <NativeSelectOption key={c.id} value={c.id}>
              {c.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        {cadence === "after" ? (
          <Input
            aria-label="Days after"
            type="number"
            min={1}
            max={365}
            className="w-20"
            value={days}
            onChange={(e) => onDays(Number(e.target.value) || 1)}
          />
        ) : null}
      </span>
    </Field>
  );
}

/** A responsibility of the seat, or a new one by name (`""` = new). */
function ResponsibilityFields({
  api,
  company,
  seatId,
  value,
  name,
  onValue,
  onName,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  value: string;
  name: string;
  onValue: (id: string) => void;
  onName: (n: string) => void;
}) {
  const ids = { pick: useId(), name: useId() };
  const q = useInfiniteQuery(responsibilitiesQuery(api, company.id, seatId));
  const list = (q.data?.pages.flatMap((p) => p.data) ?? []).filter((r) => r.status === "active");
  return (
    <>
      <Field>
        <FieldLabel htmlFor={ids.pick}>Responsibility</FieldLabel>
        <NativeSelect id={ids.pick} value={value} onChange={(e) => onValue(e.target.value)}>
          <NativeSelectOption value="">A new responsibility…</NativeSelectOption>
          {list.map((r) => (
            <NativeSelectOption key={r.id} value={r.id}>
              {r.name}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </Field>
      {value === "" ? (
        <Field>
          <FieldLabel htmlFor={ids.name}>New responsibility</FieldLabel>
          <Input id={ids.name} required value={name} onChange={(e) => onName(e.target.value)} />
        </Field>
      ) : null}
    </>
  );
}

function AddRoutineDialog({
  api,
  company,
  seatId,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const ids = { title: useId(), board: useId() };
  const boards = useQuery(boardsQuery(api, co));
  const [title, setTitle] = useState("");
  const [board, setBoard] = useState("");
  const [resp, setResp] = useState("");
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState<CadenceId>("weekly");
  const [days, setDays] = useState(7);
  const firstBoard = boards.data?.data[0]?.id ?? "";
  useEffect(() => {
    if (!board && firstBoard) setBoard(firstBoard);
  }, [board, firstBoard]);
  const save = useMutation({
    mutationFn: () =>
      api.client.seats.routinesCreate({
        params: { co, id: seatId },
        body: {
          ...(resp ? { responsibility_id: resp } : { responsibility_name: name || title }),
          ...cadenceBody(cadence, days),
          board_id: board,
          task_template: { title },
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: processKeys.routines(co, seatId) });
      toast.add({ title: r.proposed ? "Sent for approval" : "Routine added", type: "success" });
      onClose();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Add routine</DialogTitle>
            <DialogDescription>
              The same task, created on a cadence for this seat.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.title}>Task</FieldLabel>
            <Input
              id={ids.title}
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <ResponsibilityFields
            api={api}
            company={company}
            seatId={seatId}
            value={resp}
            name={name}
            onValue={setResp}
            onName={setName}
          />
          <Field>
            <FieldLabel htmlFor={ids.board}>On board</FieldLabel>
            <NativeSelect id={ids.board} value={board} onChange={(e) => setBoard(e.target.value)}>
              {(boards.data?.data ?? []).map((b) => (
                <NativeSelectOption key={b.id} value={b.id}>
                  {b.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <CadenceFields cadence={cadence} days={days} onCadence={setCadence} onDays={setDays} />
          {save.isError ? (
            <Notice tone="danger">{problemText(save.error, "Not added")}</Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !title.trim() || !board}>
              Add routine
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TakeOverDialog({
  api,
  company,
  routine,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  routine: Routine;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const seats = useOpenSeats(api, co);
  const ids = { scope: useId(), seat: useId() };
  const own = routine.responsibility.seat_id;
  const [scope, setScope] = useState<"occurrence" | "all">("all");
  const [seat, setSeat] = useState(routine.control?.taken_over_by_seat_id ? own : "");
  const firstOther = seats.find((s) => s.id !== own)?.id ?? "";
  useEffect(() => {
    if (!seat && firstOther) setSeat(firstOther);
  }, [seat, firstOther]);
  const save = useMutation({
    mutationFn: () =>
      api.client.recurringWork.takeOver({
        params: { co, id: routine.recurring_work.id },
        body: { scope, seat_id: seat },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: processKeys.routines(co, own) });
      toast.add({ title: seat === own ? "Handed back" : "Taken over", type: "success" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Take over “{routine.recurring_work.task_template.title}”</DialogTitle>
            <DialogDescription>
              Someone else does it: the open one only, or from now on. Choose the routine's own seat
              to hand it back.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.scope}>Which</FieldLabel>
            <NativeSelect
              id={ids.scope}
              value={scope}
              onChange={(e) => setScope(e.target.value as "occurrence" | "all")}
            >
              <NativeSelectOption value="all">Every one from now on</NativeSelectOption>
              <NativeSelectOption value="occurrence">Only the open one</NativeSelectOption>
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.seat}>By</FieldLabel>
            <NativeSelect id={ids.seat} value={seat} onChange={(e) => setSeat(e.target.value)}>
              {seats.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.title}
                  {s.id === own ? " (hand back)" : ""}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {save.isError ? (
            <Notice tone="danger">{problemText(save.error, "Not taken over")}</Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !seat}>
              {seat === own ? "Hand back" : "Take over"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RoutineHistory({
  api,
  company,
  routine,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  routine: Routine;
  onClose: () => void;
}) {
  const q = useQuery(routineHistoryQuery(api, company.id, routine.recurring_work.id));
  const rows = q.data?.data ?? [];
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md" data-slot="routine-history">
        <DialogHeader>
          <DialogTitle>History: {routine.recurring_work.task_template.title}</DialogTitle>
          <DialogDescription>Each occurrence and control, newest first.</DialogDescription>
        </DialogHeader>
        {q.isPending ? <Loading label="Loading history" rows={2} /> : null}
        {q.isError ? <LoadError what="History" error={q.error} /> : null}
        {q.data && !rows.length ? (
          <p className="text-sm text-muted-foreground">Nothing yet.</p>
        ) : null}
        <ul className="flex max-h-80 flex-col gap-1.5 overflow-y-auto text-sm">
          {rows.map((h) => (
            <li key={h.id} className="flex justify-between gap-3">
              <span>{OUTCOME_TEXT[h.outcome] ?? h.outcome}</span>
              <span className="text-muted-foreground">{formatDateTime(h.created_at)}</span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

// Repeat… -------------------------------------------------------------------------------------

type RepeatTask = { id: string; title: string; assignee_seat_ids?: readonly string[] };
const listeners = new Set<(t: RepeatTask) => void>();

/** Opens the Repeat… dialog for `task` (the drawer's menu item). */
export function openRepeat(task: RepeatTask): void {
  for (const l of listeners) l(task);
}

/** Mounts the Repeat… dialog the drawer's menu opens (one per drawer). */
export function RepeatHost({ api, company }: { api: Api; company: CompanyAccess }) {
  const [task, setTask] = useState<RepeatTask | null>(null);
  useEffect(() => {
    listeners.add(setTask);
    return () => {
      listeners.delete(setTask);
    };
  }, []);
  return task ? (
    <RepeatDialog api={api} company={company} task={task} onClose={() => setTask(null)} />
  ) : null;
}

export function RepeatDialog({
  api,
  company,
  task,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  task: RepeatTask;
  onClose: () => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const seats = useOpenSeats(api, co);
  const id = useId();
  const [seat, setSeat] = useState(task.assignee_seat_ids?.[0] ?? "");
  const [resp, setResp] = useState("");
  const [name, setName] = useState(task.title);
  const [cadence, setCadence] = useState<CadenceId>("weekly");
  const [days, setDays] = useState(7);
  const firstSeat = seats[0]?.id ?? "";
  useEffect(() => {
    if (!seat && firstSeat) setSeat(firstSeat);
  }, [seat, firstSeat]);
  const save = useMutation({
    mutationFn: () =>
      api.client.tasks.repeat({
        params: { co, id: task.id },
        body: {
          seat_id: seat,
          ...(resp ? { responsibility_id: resp } : { responsibility_name: name }),
          ...cadenceBody(cadence, days),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: processKeys.allRoutines(co) });
      toast.add({ title: "It repeats now", type: "success" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-md" data-slot="repeat-dialog">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Repeat “{task.title}”</DialogTitle>
            <DialogDescription>
              This task becomes a routine: a new one on the cadence, for the seat you choose.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>For the seat</FieldLabel>
            <NativeSelect
              id={id}
              value={seat}
              onChange={(e) => {
                setSeat(e.target.value);
                setResp("");
              }}
            >
              {seats.map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.title}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {seat ? (
            <ResponsibilityFields
              api={api}
              company={company}
              seatId={seat}
              value={resp}
              name={name}
              onValue={setResp}
              onName={setName}
            />
          ) : null}
          <CadenceFields cadence={cadence} days={days} onCadence={setCadence} onDays={setDays} />
          {save.isError ? (
            <Notice tone="danger">{problemText(save.error, "Not repeated")}</Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !seat}>
              Repeat
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
